// @ts-nocheck
/**
 * MagFlow Template
 * 功能：滚动淡入（GSAP / ScrollTrigger）；KV 导航滚过 KV 后吸顶滑入，滚回收起；手机端品牌下拉；
 * 产品卡：桌面 5+ 左右切换，手机 View More 展开；
 * 一图懂整块淡入与循环视频；TVC Watch Now YouTube 弹窗；液冷卖点扫光标题与桌面卡屏分屏；风冷卖点视频；
 * 安全耐用轮播与第一张卡弹窗；充电场景手风琴；场景图轮播；
 * 留邮表单。
 * 断点：与 CSS 一致（max-width: 1023.98px）。
 */
(function () {
  'use strict';

  const TAG_NAME = 'magflow-template-section';
  if (customElements.get(TAG_NAME)) return;
  const FORM_SUBMIT_URL = 'https://ecf.cirkleinc.com/api/form-submit';

  const MOBILE_MQ = '(max-width: 1023.98px)';
  const MFT_KV_NAV_ANCHOR_GAP_BELOW_NAV_PX = 8;
  const MFT_KV_NAV_ANCHOR_EXTRA_SCROLL_PX = 20;
  /* 与 CSS --mft-* 对齐：轮播位移 / 速度条 / 扫光 / 液冷交叉淡 / 自动切 */
  const MFT_MOTION = Object.freeze({
    slideMs: 400,
    meterMs: 1600,
    sweepMs: 1200,
    techCrossfadeMs: 500,
    autoplayMs: 5000,
    resizeDebounceMs: 220,
    swiperPollMs: 80
  });
  const MFT_TITLE_SWEEP = Object.freeze({
    duration: MFT_MOTION.sweepMs,
    startPos: [100, 144, 158, 200, 220],
    endPos: [-120, -76, -62, -20, 0]
  });
  /* 节奏对齐 Nexode Air：只播一次，标题→内容错开 0.18s */
  const MFT_REVEAL = Object.freeze({
    y: 26,
    duration: 0.88,
    ease: 'power2.out',
    start: 'top 70%',
    stagger: 0.18
  });

  function mftPrefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* 弹窗打开时锁滚动。iOS Safari 只改 html.overflow 挡不住背后滑动，需同时钉住 body */
  function mftLockPageScroll() {
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }

  /* clipboard API 失败时回退 execCommand，避免复制成功态空转 */
  function mftCopyText(text, onDone) {
    const done = typeof onDone === 'function' ? onDone : () => {};
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;left:-9999px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch (_) {}
      ta.remove();
      done();
    };
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text).then(done).catch(fallback);
      return;
    }
    fallback();
  }

  /**
   * KV 导航：fixed 不占文档流；初始隐藏，滚过 KV 底部后滑入；滚回 KV 上方再收起。
   * 手机端：品牌 + 下箭头展开 Highlights / Technology，右侧 Buy Now 保持可见。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>, reduced: boolean }} ctx
   */
  function initMagflowKvNav(root, ctx) {
    const nav = root.querySelector('[data-mft-kv-nav]');
    if (!(nav instanceof HTMLElement)) {
      return;
    }

    const kvEl = root.querySelector('[data-mft-kv]');
    const buyBtn = nav.querySelector('[data-mft-kv-nav-buy]');
    const buyTarget = root.querySelector('#mft-editions') || document.querySelector('#mft-editions');
    const dropdownToggle = nav.querySelector('[data-mft-kv-nav-dropdown-toggle]');
    const dropdownPanel = nav.querySelector('[data-mft-kv-nav-dropdown]');
    let closeMobileKvMenu = () => {};

    let kvRevealThreshold = 0;

    const measureKvRevealThreshold = () => {
      if (kvEl instanceof HTMLElement) {
        const rect = kvEl.getBoundingClientRect();
        const sy0 = window.scrollY;
        kvRevealThreshold = Math.max(0, Math.round(rect.bottom + sy0));
        return;
      }
      kvRevealThreshold = 0;
    };

    const closeDropdownForStickyHide = () => {
      closeMobileKvMenu();
      if (dropdownPanel instanceof HTMLElement && dropdownPanel.dataset.mftKvDdMounted !== '1') {
        dropdownPanel.hidden = true;
      }
    };

    const finishLeave = () => {
      nav.classList.remove('is-kv-sticky', 'is-kv-sticky-leaving', 'is-kv-sticky-settled');
      nav.classList.add('is-kv-nav-before-kv');
      nav.setAttribute('aria-hidden', 'true');
      closeDropdownForStickyHide();
      window.requestAnimationFrame(() => {
        measureKvRevealThreshold();
      });
    };

    const onNavAnimEnd = (ev) => {
      if (!(ev instanceof AnimationEvent) || ev.target !== nav) {
        return;
      }
      if (ev.animationName === 'ug-mft-kv-nav-slide-out') {
        finishLeave();
        return;
      }
      /* 滑入结束后摘掉 animation，避免父级 opacity 合成层让 Buy hover 闪一下 */
      if (ev.animationName === 'ug-mft-kv-nav-slide-in') {
        nav.classList.add('is-kv-sticky-settled');
      }
    };

    const startLeave = () => {
      if (!nav.classList.contains('is-kv-sticky') || nav.classList.contains('is-kv-sticky-leaving')) {
        return;
      }
      closeDropdownForStickyHide();
      nav.classList.remove('is-kv-sticky-settled');
      if (ctx.reduced) {
        finishLeave();
        return;
      }
      nav.classList.add('is-kv-sticky-leaving');
    };

    measureKvRevealThreshold();

    let ticking = false;
    const updateStickyState = () => {
      ticking = false;
      const sy = window.scrollY;
      const wantVisible = sy >= kvRevealThreshold;

      if (nav.classList.contains('is-kv-sticky-leaving')) {
        if (wantVisible) {
          nav.classList.remove('is-kv-sticky-leaving');
          nav.classList.add('is-kv-sticky', 'is-kv-sticky-settled');
        }
        return;
      }

      if (wantVisible) {
        if (nav.classList.contains('is-kv-nav-before-kv')) {
          nav.classList.remove('is-kv-nav-before-kv');
          nav.removeAttribute('aria-hidden');
          if (!ctx.reduced) {
            void nav.offsetHeight;
          }
          nav.classList.remove('is-kv-sticky-leaving', 'is-kv-sticky-settled');
          nav.classList.add('is-kv-sticky');
          if (ctx.reduced) {
            nav.classList.add('is-kv-sticky-settled');
          }
        } else if (!nav.classList.contains('is-kv-sticky')) {
          nav.classList.add('is-kv-sticky');
          if (ctx.reduced) {
            nav.classList.add('is-kv-sticky-settled');
          }
        }
      } else if (nav.classList.contains('is-kv-sticky')) {
        startLeave();
      } else if (!nav.classList.contains('is-kv-nav-before-kv')) {
        nav.classList.add('is-kv-nav-before-kv');
        nav.setAttribute('aria-hidden', 'true');
      }
    };

    const requestStickyUpdate = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(updateStickyState);
    };

    const onResize = () => {
      measureKvRevealThreshold();
      requestStickyUpdate();
    };

    requestStickyUpdate();
    nav.addEventListener('animationend', onNavAnimEnd);
    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    ctx.cleanups.push(() => window.removeEventListener('scroll', requestStickyUpdate));
    ctx.cleanups.push(() => window.removeEventListener('resize', onResize));
    ctx.cleanups.push(() => {
      nav.removeEventListener('animationend', onNavAnimEnd);
      nav.classList.remove(
        'is-kv-sticky',
        'is-kv-sticky-leaving',
        'is-kv-sticky-settled',
        'is-kv-nav-before-kv'
      );
      nav.removeAttribute('aria-hidden');
    });

    const jumpTo = (targetEl) => {
      if (!(targetEl instanceof HTMLElement)) {
        return;
      }
      const sy = window.scrollY;
      const navH = Math.max(0, Math.round(nav.getBoundingClientRect().height));
      const targetTop = targetEl.getBoundingClientRect().top + sy;
      const nextTop = Math.max(
        0,
        targetTop - navH - MFT_KV_NAV_ANCHOR_GAP_BELOW_NAV_PX + MFT_KV_NAV_ANCHOR_EXTRA_SCROLL_PX
      );
      window.scrollTo({
        top: nextTop,
        behavior: ctx.reduced ? 'auto' : 'smooth'
      });
    };

    const getHashFromHref = (href) => {
      if (typeof href !== 'string' || href.length === 0) {
        return '';
      }
      if (href.startsWith('#')) {
        return href;
      }
      try {
        const url = new URL(href, window.location.href);
        return url.hash || '';
      } catch (_) {
        return '';
      }
    };

    const getTargetByHash = (hash) => {
      if (typeof hash !== 'string' || !hash.startsWith('#')) {
        return null;
      }
      const target = root.querySelector(hash) || document.querySelector(hash);
      return target instanceof HTMLElement ? target : null;
    };

    const anchorEls = Array.from(nav.querySelectorAll('[data-mft-kv-nav-anchor]'));
    const onAnchorClick = (ev) => {
      const link = ev.currentTarget;
      if (!(link instanceof HTMLAnchorElement)) {
        return;
      }
      const hash = getHashFromHref(link.getAttribute('href') || '');
      const target = getTargetByHash(hash);
      if (!target) {
        return;
      }
      ev.preventDefault();
      if (dropdownPanel instanceof HTMLElement && dropdownPanel.contains(link)) {
        closeMobileKvMenu();
      }
      jumpTo(target);
    };
    anchorEls.forEach((el) => {
      el.addEventListener('click', onAnchorClick);
      ctx.cleanups.push(() => el.removeEventListener('click', onAnchorClick));
    });

    const onBuyNowClick = (ev) => {
      if (!(buyTarget instanceof HTMLElement)) {
        return;
      }
      ev.preventDefault();
      jumpTo(buyTarget);
    };
    if (buyBtn instanceof HTMLElement) {
      buyBtn.addEventListener('click', onBuyNowClick);
      ctx.cleanups.push(() => buyBtn.removeEventListener('click', onBuyNowClick));
    }

    if (dropdownToggle instanceof HTMLElement && dropdownPanel instanceof HTMLElement) {
      const mqMobile = window.matchMedia(MOBILE_MQ);

      dropdownPanel.removeAttribute('hidden');
      dropdownPanel.dataset.mftKvDdMounted = '1';

      const closeDropdown = () => {
        nav.classList.remove('is-kv-dropdown-open');
        dropdownToggle.setAttribute('aria-expanded', 'false');
      };

      const openDropdown = () => {
        nav.classList.add('is-kv-dropdown-open');
        dropdownToggle.setAttribute('aria-expanded', 'true');
      };

      const toggleDropdown = () => {
        if (nav.classList.contains('is-kv-dropdown-open')) {
          closeDropdown();
          return;
        }
        openDropdown();
      };

      const onToggleClick = (ev) => {
        if (!mqMobile.matches) return;
        ev.preventDefault();
        ev.stopPropagation();
        toggleDropdown();
      };

      const onDocumentClick = (ev) => {
        if (!(ev.target instanceof Node)) {
          return;
        }
        const t = ev.target;
        if (t === dropdownToggle || dropdownToggle.contains(t)) {
          return;
        }
        if (t === dropdownPanel || dropdownPanel.contains(t)) {
          return;
        }
        if (!nav.contains(t)) {
          window.requestAnimationFrame(() => {
            closeDropdown();
          });
        }
      };

      const onKeyDown = (ev) => {
        if (ev.key === 'Escape') {
          closeDropdown();
        }
      };

      const onViewportChange = () => {
        if (!mqMobile.matches) {
          closeDropdown();
        }
      };

      closeDropdown();
      dropdownToggle.addEventListener('click', onToggleClick);
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('keydown', onKeyDown);
      window.addEventListener('resize', onViewportChange, { passive: true });
      ctx.cleanups.push(() => dropdownToggle.removeEventListener('click', onToggleClick));
      ctx.cleanups.push(() => document.removeEventListener('click', onDocumentClick));
      ctx.cleanups.push(() => document.removeEventListener('keydown', onKeyDown));
      ctx.cleanups.push(() => window.removeEventListener('resize', onViewportChange));
      ctx.cleanups.push(() => closeDropdown());

      closeMobileKvMenu = closeDropdown;
    }
  }

  /**
   * 产品卡：仅桌面且卡数 > 4 时启用 Swiper（4 列 + 溢出卡半透明）。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowEditionsSwiper(root, ctx) {
    const { cleanups } = ctx;
    const sections = root.querySelectorAll('[data-mft-editions]');
    if (!sections.length) return;

    const DESKTOP_MQ = '(min-width: 1024px)';
    const instances = [];

    sections.forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const el = section.querySelector('[data-mft-editions-swiper]');
      if (!(el instanceof HTMLElement)) return;
      instances.push({
        section,
        el,
        navWrap: section.querySelector('[data-mft-editions-nav]'),
        prev: section.querySelector('[data-mft-editions-nav-prev]'),
        next: section.querySelector('[data-mft-editions-nav-next]'),
        swiper: null
      });
    });
    if (!instances.length) return;

    let resizeT = 0;
    let swiperWait = 0;

    const hideNav = (inst) => {
      if (inst.navWrap instanceof HTMLElement) {
        inst.navWrap.classList.remove('is-visible');
        inst.navWrap.setAttribute('aria-hidden', 'true');
      }
    };

    const clearActiveSlides = (inst) => {
      inst.el.querySelectorAll('.is-mft-editions-active').forEach((n) => {
        n.classList.remove('is-mft-editions-active');
      });
    };

    /* 高亮当前窗口内的 4 张；不依赖 fully-visible，避免 1024 把仍在视口的卡判半透明 */
    const markActiveSlides = (sw) => {
      if (!sw) return;
      const perView = Math.max(1, Math.round(Number(sw.params.slidesPerView) || 4));
      const slides = Array.from(sw.slides || []);
      const total = slides.length;
      let start = sw.activeIndex || 0;
      const maxStart = Math.max(0, total - perView);
      if (start > maxStart) start = maxStart;
      slides.forEach((slide, i) => {
        if (!(slide instanceof HTMLElement)) return;
        slide.classList.toggle('is-mft-editions-active', i >= start && i < start + perView);
      });
    };

    const destroyOne = (inst) => {
      if (inst.swiper) {
        try {
          inst.swiper.destroy(true, true);
        } catch (_) {}
        inst.swiper = null;
      }
      clearActiveSlides(inst);
      inst.section.classList.remove('is-editions-swiper-inited');
      hideNav(inst);
    };

    const mountOne = (inst) => {
      if (typeof window.Swiper !== 'function') return;
      const mq = window.matchMedia(DESKTOP_MQ);
      const count = inst.el.querySelectorAll('.swiper-slide').length;
      const showNav = mq.matches && count > 4;
      destroyOne(inst);
      if (!showNav) return;

      if (inst.navWrap instanceof HTMLElement) {
        inst.navWrap.classList.add('is-visible');
        inst.navWrap.setAttribute('aria-hidden', 'false');
      }

      const opts = {
        spaceBetween: 20,
        slidesPerView: 4,
        watchOverflow: true,
        slidesPerGroup: 1,
        on: {
          init(sw) {
            sw.update();
            markActiveSlides(sw);
          },
          slideChange(sw) {
            markActiveSlides(sw);
          },
          resize(sw) {
            sw.update();
            markActiveSlides(sw);
          }
        }
      };
      if (inst.prev && inst.next) {
        opts.navigation = { prevEl: inst.prev, nextEl: inst.next };
      }
      try {
        inst.swiper = new window.Swiper(inst.el, opts);
        inst.section.classList.add('is-editions-swiper-inited');
        markActiveSlides(inst.swiper);
      } catch (_) {}
    };

    const mount = () => {
      instances.forEach(mountOne);
    };

    const onResize = () => {
      clearTimeout(resizeT);
      resizeT = window.setTimeout(mount, MFT_MOTION.resizeDebounceMs);
    };

    if (typeof window.Swiper === 'function') {
      mount();
    } else {
      swiperWait = window.setInterval(() => {
        if (typeof window.Swiper === 'function') {
          clearInterval(swiperWait);
          swiperWait = 0;
          mount();
        }
      }, MFT_MOTION.swiperPollMs);
      const waitMax = window.setTimeout(() => {
        if (swiperWait) {
          clearInterval(swiperWait);
          swiperWait = 0;
        }
      }, 10000);
      cleanups.push(() => clearTimeout(waitMax));
    }

    window.addEventListener('resize', onResize, { passive: true });
    cleanups.push(() => {
      clearTimeout(resizeT);
      if (swiperWait) clearInterval(swiperWait);
      window.removeEventListener('resize', onResize);
      instances.forEach(destroyOne);
    });
  }

  /**
   * 手机端：5 个及以上先显示 2 张，点 View More 展开全部。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowEditionsMoViewMore(root, ctx) {
    const { cleanups } = ctx;
    root.querySelectorAll('[data-mft-editions][data-mft-editions-has-many]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const btn = section.querySelector('[data-mft-editions-mo-view-more]');
      if (!(btn instanceof HTMLButtonElement)) return;
      const onClick = () => {
        section.classList.add('is-mft-mo-expanded');
        btn.setAttribute('aria-expanded', 'true');
      };
      btn.addEventListener('click', onClick);
      cleanups.push(() => btn.removeEventListener('click', onClick));
    });
  }

  /**
   * 产品卡优惠码复制。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowEditionsCodeCopy(root, ctx) {
    const { cleanups } = ctx;
    const onClick = (e) => {
      const btn =
        e.target instanceof Element ? e.target.closest('[data-mft-editions-code-copy]') : null;
      if (!btn || !root.contains(btn)) return;
      const code = btn.getAttribute('data-code');
      if (!code) return;
      const copiedText = btn.getAttribute('data-copied-text');
      const copyText = btn.getAttribute('data-copy-text');
      mftCopyText(code, () => {
        if (copiedText) btn.textContent = copiedText;
        window.setTimeout(() => {
          if (copyText) btn.textContent = copyText;
        }, 2000);
      });
    };
    root.addEventListener('click', onClick);
    cleanups.push(() => root.removeEventListener('click', onClick));
  }

  /** YouTube 链接转 embed（带 autoplay） */
  function mftNormalizeYoutubeEmbed(rawUrl) {
    const url = String(rawUrl || '').trim();
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      let id = '';
      const host = parsed.hostname.toLowerCase();
      if (host.includes('youtu.be')) {
        id = parsed.pathname.replace(/^\//, '').split('/')[0] || '';
      } else if (host.includes('youtube.com') || host.includes('youtube-nocookie.com')) {
        if (parsed.pathname.startsWith('/embed/')) {
          id = parsed.pathname.split('/')[2] || '';
        } else if (parsed.pathname.startsWith('/shorts/')) {
          id = parsed.pathname.split('/')[2] || '';
        } else {
          id = parsed.searchParams.get('v') || '';
        }
      }
      if (!id) {
        if (/^[a-zA-Z0-9_-]{6,}$/.test(url)) {
          return `https://www.youtube.com/embed/${url}?autoplay=1&rel=0&playsinline=1`;
        }
        return '';
      }
      return `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
    } catch (_) {
      return '';
    }
  }

  /**
   * 等 GSAP / ScrollTrigger 就绪；超时则走无动效兜底。
   *
   * @param {(ok: boolean) => void} cb
   * @param {number} [timeoutMs]
   */
  function whenGsapReady(cb, timeoutMs) {
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      cb(ok);
    };
    if (window.gsap && window.ScrollTrigger) {
      finish(true);
      return () => {};
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (window.gsap && window.ScrollTrigger) {
        window.clearInterval(timer);
        finish(true);
        return;
      }
      if (Date.now() - started > (timeoutMs || 2500)) {
        window.clearInterval(timer);
        finish(false);
      }
    }, 80);
    return () => {
      settled = true;
      window.clearInterval(timer);
    };
  }

  let mftRefreshRaf = 0;

  /* 对齐 Nexode Air：多处改布局时合并到下一帧再 refresh，避免同帧重复计算 */
  function scheduleMftScrollTriggerRefresh() {
    const ScrollTrigger = window.ScrollTrigger;
    if (!ScrollTrigger || typeof ScrollTrigger.refresh !== 'function') return;
    if (mftRefreshRaf) return;
    mftRefreshRaf = window.requestAnimationFrame(() => {
      mftRefreshRaf = 0;
      try {
        ScrollTrigger.refresh();
      } catch (_) {}
    });
  }

  /* refresh 把 tween 打回 0 时钉在终态，避免模块被重新藏掉 */
  function mftGuardRevealOnRefresh(self) {
    const anim = self && self.animation;
    if (!anim || self.progress <= 0 || anim.progress() > 0) return;
    anim.progress(1);
  }

  /* 不是从页顶进来：视口上方（已滚过）或当前看得见的，直接终态，避免往回滚再被藏掉 */
  function mftRevealShouldSnapToEnd(el) {
    if (!(el instanceof HTMLElement)) return false;
    if ((window.scrollY || 0) <= 80) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    if (rect.bottom <= 0) return true;
    return rect.top < vh && rect.bottom > 0;
  }

  function playMftTitleSweep(el) {
    if (!(el instanceof HTMLElement)) return;
    if (!el.hasAttribute('data-mft-title-sweep')) return;
    if (typeof el._mftPlaySweep === 'function') el._mftPlaySweep();
  }

  function onMftRevealComplete(el) {
    if (el instanceof HTMLElement) el.dataset.mftRevealDone = '1';
    playMftRevealMedia(el);
    playMftTitleSweep(el);
  }

  /**
   * 桌面液冷卡屏交给 ScrollTrigger pin，后段淡入才能按占位后的起点计算。
   * 与 Nexode Air「先注册 pin，再跑渐显」同一原因。
   */
  function bindMagflowTechPinForReveal(root, ctx) {
    const { cleanups, registered } = ctx;
    const ScrollTrigger = window.ScrollTrigger;
    const pin = root.querySelector('[data-mft-tech-pin]');
    const stage = pin instanceof HTMLElement ? pin.querySelector('.ug-magflow-template__tech-stage') : null;
    if (!(pin instanceof HTMLElement) || !(stage instanceof HTMLElement) || !ScrollTrigger) {
      return;
    }

    const mm = ScrollTrigger.matchMedia({
      '(min-width: 1024px)': () => {
        root.classList.add('is-mft-tech-st-pin');
        const st = ScrollTrigger.create({
          trigger: stage,
          start: 'top top',
          end: () => '+=' + Math.max(1, stage.offsetHeight),
          pin: stage,
          pinSpacing: true,
          anticipatePin: 0,
          invalidateOnRefresh: true,
          refreshPriority: 1
        });
        registered.push(st);
        return () => {
          root.classList.remove('is-mft-tech-st-pin');
          try {
            st.kill();
          } catch (_) {}
        };
      }
    });
    cleanups.push(() => {
      try {
        mm.revert();
      } catch (_) {}
      root.classList.remove('is-mft-tech-st-pin');
    });
  }

  /**
   * 淡入完成后的接续：循环视频 / 液冷首屏 / 速度条。
   *
   * @param {HTMLElement} el
   */
  function playMftRevealMedia(el) {
    if (!(el instanceof HTMLElement)) return;
    const kind = el.getAttribute('data-mft-reveal-play') || '';
    if (kind === 'speed-meter') {
      el.querySelectorAll('[data-mft-speed-meter]').forEach((meter) => {
        if (typeof meter._mftPlaySpeed === 'function') meter._mftPlaySpeed();
      });
      return;
    }
    if (kind === 'tech-liq') {
      const pin = el.closest('[data-mft-tech-pin]');
      if (pin instanceof HTMLElement) {
        pin.dataset.mftLiqReady = '1';
        if (typeof pin._mftTryLiqPlay === 'function') pin._mftTryLiqPlay();
      }
      return;
    }
    el.querySelectorAll('[data-mft-reveal-wait]').forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      video.dataset.mftRevealReady = '1';
      if (typeof video._mftPlayLoop === 'function') video._mftPlayLoop();
    });
  }

  /**
   * stagger 根下最外层 `data-mft-reveal`，避免嵌套重复播。
   *
   * @param {HTMLElement} staggerRoot
   * @returns {HTMLElement[]}
   */
  function collectMftStaggerRevealElements(staggerRoot) {
    if (!(staggerRoot instanceof HTMLElement)) return [];
    /** @type {HTMLElement[]} */
    const out = [];
    staggerRoot.querySelectorAll(':scope [data-mft-reveal]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      let p = el.parentElement;
      while (p && p !== staggerRoot) {
        if (p.hasAttribute('data-mft-reveal')) return;
        p = p.parentElement;
      }
      if (p === staggerRoot) out.push(el);
    });
    return out;
  }

  /**
   * 减少动态 / 无 GSAP 时直接终态，并接续视频与进度条。
   *
   * @param {HTMLElement} root
   */
  function showMftRevealEndState(root) {
    root.querySelectorAll('[data-mft-reveal]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    root.querySelectorAll('[data-mft-reveal-play]').forEach((el) => {
      if (el instanceof HTMLElement) playMftRevealMedia(el);
    });
    root.querySelectorAll('[data-mft-title-sweep]').forEach((el) => {
      if (el instanceof HTMLElement) playMftTitleSweep(el);
    });
  }

  /**
   * 全局滚动淡入：只播一次。大标题扫光接在该元素淡入完成之后。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>, reduced: boolean, hasGsap: boolean }} ctx
   */
  function initMagflowScrollReveal(root, ctx) {
    const { cleanups, reduced, hasGsap } = ctx;
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;

    if (reduced || !hasGsap || !gsap || !ScrollTrigger) {
      showMftRevealEndState(root);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    root.setAttribute('data-mft-reveal-armed', '');

    const fromVars = { opacity: 0, y: MFT_REVEAL.y };
    const { duration, ease, start, stagger } = MFT_REVEAL;
    /** @type {Array<{ kill?: () => void }>} */
    const registered = [];

    const snapRevealEl = (el) => {
      gsap.set(el, { opacity: 1, y: 0 });
      el.dataset.mftRevealDone = '1';
      onMftRevealComplete(el);
    };

    /* 先挂卡屏 pin，再算后面模块的淡入起点 */
    bindMagflowTechPinForReveal(root, { cleanups, registered });

    const track = (tween) => {
      if (tween && tween.scrollTrigger) registered.push(tween.scrollTrigger);
    };

    const revealTriggerVars = (triggerEl) => ({
      trigger: triggerEl,
      start,
      once: true,
      toggleActions: 'play none none none',
      onRefresh: mftGuardRevealOnRefresh
    });

    root.querySelectorAll('[data-mft-reveal-stagger]').forEach((staggerRoot) => {
      if (!(staggerRoot instanceof HTMLElement)) return;
      const children = collectMftStaggerRevealElements(staggerRoot);
      if (!children.length) return;
      if (mftRevealShouldSnapToEnd(staggerRoot)) {
        children.forEach((child) => snapRevealEl(child));
        staggerRoot.dataset.mftRevealDone = '1';
        return;
      }

      const tl = gsap.timeline({
        scrollTrigger: revealTriggerVars(staggerRoot),
        onComplete: () => {
          staggerRoot.dataset.mftRevealDone = '1';
        }
      });
      if (tl.scrollTrigger) registered.push(tl.scrollTrigger);

      children.forEach((child, index) => {
        gsap.set(child, fromVars);
        tl.to(
          child,
          {
            opacity: 1,
            y: 0,
            duration,
            ease,
            onComplete: () => onMftRevealComplete(child)
          },
          index * stagger
        );
      });
    });

    root.querySelectorAll('[data-mft-reveal]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.closest('[data-mft-reveal-stagger]')) return;
      if (el.getAttribute('data-mft-reveal-when') === 'tech-heat') {
        gsap.set(el, fromVars);
        const playHeat = () => {
          if (el.dataset.mftHeatRevealed === '1') return;
          el.dataset.mftHeatRevealed = '1';
          gsap.to(el, { opacity: 1, y: 0, duration, ease });
        };
        el._mftPlayHeatReveal = playHeat;
        if (mftRevealShouldSnapToEnd(el)) {
          playHeat();
          return;
        }
        const pin = el.closest('[data-mft-tech-pin]');
        const mm = ScrollTrigger.matchMedia({
          [MOBILE_MQ]: () => {
            const tween = gsap.to(el, {
              opacity: 1,
              y: 0,
              duration,
              ease,
              onStart: () => {
                el.dataset.mftHeatRevealed = '1';
              },
              scrollTrigger: revealTriggerVars(el)
            });
            track(tween);
          },
          '(min-width: 1024px)': () => {
            if (pin && pin.dataset.mftHeatReady === '1') playHeat();
          }
        });
        cleanups.push(() => {
          try {
            mm.revert();
          } catch (_) {}
        });
        return;
      }

      if (mftRevealShouldSnapToEnd(el)) {
        snapRevealEl(el);
        return;
      }

      gsap.set(el, fromVars);
      const tween = gsap.to(el, {
        opacity: 1,
        y: 0,
        duration,
        ease,
        onComplete: () => onMftRevealComplete(el),
        scrollTrigger: revealTriggerVars(el)
      });
      track(tween);
    });

    scheduleMftScrollTriggerRefresh();

    cleanups.push(() => {
      registered.forEach((st) => {
        try {
          if (st && typeof st.kill === 'function') st.kill();
        } catch (_) {}
      });
      root.removeAttribute('data-mft-reveal-armed');
    });
  }

  /**
   * 一图懂 / TVC 循环视频，以及 tec-chart 单次播放：进视口播放，离开暂停。液冷卖点由卡屏逻辑单独控制。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowLoopVideos(root, ctx) {
    const { cleanups } = ctx;
    const videos = Array.from(
      root.querySelectorAll('[data-mft-hl-loop-video], [data-mft-tec-loop-video]')
    ).filter((el) => el instanceof HTMLVideoElement);
    if (!videos.length) return;

    const playVideo = (video) => {
      const playOnce = Boolean(video.closest('.ug-magflow-template__tec-chart'));
      if (playOnce && video.ended) return;
      if (!video.paused && !video.ended) return;
      video.muted = true;
      video.loop = !playOnce;
      video.playsInline = true;
      const card =
        video.closest('[data-mft-hl-loop-card]') ||
        video.closest('[data-mft-tec-loop-card]') ||
        video.closest('.ug-magflow-template__tvc-media');
      const poster = card
        ? card.querySelector(
            '[data-mft-hl-loop-poster], [data-mft-tec-loop-poster], .ug-magflow-template__tvc-poster'
          )
        : null;
      const start = () => {
        video.classList.add('is-visible');
        if (poster instanceof HTMLElement) poster.classList.add('is-hidden');
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      };
      if (video.readyState >= 2) {
        start();
        return;
      }
      if (video._mftLoopLoading) return;
      video._mftLoopLoading = true;
      video.addEventListener(
        'canplay',
        () => {
          video._mftLoopLoading = false;
          start();
        },
        { once: true }
      );
      try {
        video.load();
      } catch (_) {}
    };

    const pauseVideo = (video) => {
      try {
        video.pause();
      } catch (_) {}
    };

    videos.forEach((video) => {
      video._mftPlayLoop = () => playVideo(video);
    });

    if (typeof IntersectionObserver !== 'function') {
      videos.forEach((video) => {
        if (video.hasAttribute('data-mft-reveal-wait')) return;
        playVideo(video);
      });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;
          if (entry.isIntersecting) {
            if (
              video.hasAttribute('data-mft-reveal-wait') &&
              video.dataset.mftRevealReady !== '1'
            ) {
              return;
            }
            playVideo(video);
          } else {
            pauseVideo(video);
          }
        });
      },
      { threshold: 0.15 }
    );
    videos.forEach((video) => io.observe(video));
    cleanups.push(() => {
      io.disconnect();
      videos.forEach(pauseVideo);
    });
  }

  /**
   * TVC Watch Now：YouTube 弹窗结构对齐 ug-aiot，蒙层仍用本页遮罩。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowTvcModal(root, ctx) {
    const { cleanups } = ctx;
    root.querySelectorAll('[data-mft-tvc]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const modal = section.querySelector('[data-mft-tvc-modal]');
      const iframe = section.querySelector('[data-mft-tvc-iframe]');
      const trigger = section.querySelector('[data-mft-tvc-watch]');
      if (
        !(modal instanceof HTMLElement) ||
        !(iframe instanceof HTMLIFrameElement) ||
        !(trigger instanceof HTMLElement)
      ) {
        return;
      }

      let unlockScroll = null;
      let loadTimer = 0;
      const isOpen = () => modal.classList.contains('is-open');
      const clearLoadTimer = () => {
        if (!loadTimer) return;
        window.clearTimeout(loadTimer);
        loadTimer = 0;
      };
      const revealIframe = () => {
        if (!isOpen()) return;
        clearLoadTimer();
        modal.classList.remove('is-loading');
      };
      iframe.addEventListener('load', revealIframe);
      cleanups.push(() => {
        iframe.removeEventListener('load', revealIframe);
        clearLoadTimer();
      });

      const openModal = () => {
        const embed = mftNormalizeYoutubeEmbed(trigger.getAttribute('data-mft-tvc-video-url') || '');
        if (!embed) return;
        clearLoadTimer();
        modal.classList.add('is-loading', 'is-open');
        modal.setAttribute('aria-hidden', 'false');
        iframe.src = embed;
        loadTimer = window.setTimeout(revealIframe, 6000);
        if (!unlockScroll) unlockScroll = mftLockPageScroll();
      };
      const closeModal = () => {
        clearLoadTimer();
        modal.classList.remove('is-open', 'is-loading');
        modal.setAttribute('aria-hidden', 'true');
        iframe.src = '';
        if (unlockScroll) {
          unlockScroll();
          unlockScroll = null;
        }
      };
      const onTrigger = (e) => {
        e.preventDefault();
        openModal();
      };
      trigger.addEventListener('click', onTrigger);
      cleanups.push(() => trigger.removeEventListener('click', onTrigger));
      section.querySelectorAll('[data-mft-tvc-modal-close]').forEach((btn) => {
        const onClose = (e) => {
          e.preventDefault();
          closeModal();
        };
        btn.addEventListener('click', onClose);
        cleanups.push(() => btn.removeEventListener('click', onClose));
      });
      const onEsc = (e) => {
        if (!(e instanceof KeyboardEvent) || e.key !== 'Escape') return;
        if (!isOpen()) return;
        closeModal();
      };
      window.addEventListener('keydown', onEsc);
      cleanups.push(() => {
        window.removeEventListener('keydown', onEsc);
        closeModal();
      });
    });
  }

  /**
   * 大标题扫光：有淡入的标题等淡入完成后再扫；滚出视口后重置，再进来只扫不再淡入。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>, reduced: boolean }} ctx
   */
  function initMagflowTitleSweep(root, ctx) {
    const { cleanups, reduced } = ctx;
    const titles = Array.from(root.querySelectorAll('[data-mft-title-sweep]'));
    if (!titles.length) return;

    const readColors = (title) => {
      const styles = window.getComputedStyle(title);
      return [
        styles.getPropertyValue('--ug-mft-title-start').trim() || '#ffffff',
        styles.getPropertyValue('--ug-mft-title-mid-1').trim() || '#70b8ff',
        styles.getPropertyValue('--ug-mft-title-mid-2').trim() || '#70b8ff',
        styles.getPropertyValue('--ug-mft-title-end').trim() || '#70b8ff'
      ];
    };

    const gradientAt = (progress, colors) => {
      const pos = MFT_TITLE_SWEEP.startPos.map((start, index) =>
        (start + (MFT_TITLE_SWEEP.endPos[index] - start) * progress).toFixed(2)
      );
      return (
        'linear-gradient(-90deg, ' +
        colors[0] +
        ' ' +
        pos[0] +
        '%, ' +
        colors[1] +
        ' ' +
        pos[1] +
        '%, ' +
        colors[2] +
        ' ' +
        pos[2] +
        '%, ' +
        colors[3] +
        ' ' +
        pos[3] +
        '%, ' +
        colors[0] +
        ' ' +
        pos[4] +
        '%)'
      );
    };

    const applySweep = (title, progress, colors) => {
      title.style.setProperty('--ug-mft-title-current', gradientAt(progress, colors));
    };

    const finishSweep = (title) => {
      title.style.setProperty('--ug-mft-title-current', 'var(--ug-mft-title-fill)');
    };

    titles.forEach((title) => {
      if (!(title instanceof HTMLElement)) return;
      const colors = readColors(title);
      if (reduced) {
        finishSweep(title);
        return;
      }

      let rafId = 0;
      let playing = false;

      const stopSweep = () => {
        if (rafId) {
          window.cancelAnimationFrame(rafId);
          rafId = 0;
        }
        playing = false;
      };

      const playSweep = () => {
        stopSweep();
        playing = true;
        applySweep(title, 0, colors);
        const started = performance.now();
        const tick = (now) => {
          if (!playing) return;
          const p = Math.min(1, (now - started) / MFT_TITLE_SWEEP.duration);
          applySweep(title, p, colors);
          if (p < 1) {
            rafId = window.requestAnimationFrame(tick);
            return;
          }
          finishSweep(title);
          playing = false;
          rafId = 0;
        };
        rafId = window.requestAnimationFrame(tick);
      };

      const resetSweep = () => {
        stopSweep();
        applySweep(title, 0, colors);
      };

      applySweep(title, 0, colors);

      /* 带滚动淡入的标题先等淡入结束；无淡入则进视口即扫 */
      let unlocked = !title.hasAttribute('data-mft-reveal');
      title._mftPlaySweep = () => {
        unlocked = true;
        playSweep();
      };

      if (typeof IntersectionObserver !== 'function') {
        if (unlocked) playSweep();
        cleanups.push(stopSweep);
        return;
      }

      const playObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && unlocked) playSweep();
          });
        },
        { rootMargin: '0px 0px -18% 0px', threshold: 0 }
      );
      const resetObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) resetSweep();
          });
        },
        { threshold: 0 }
      );
      playObserver.observe(title);
      resetObserver.observe(title);
      cleanups.push(() => {
        stopSweep();
        playObserver.disconnect();
        resetObserver.disconnect();
      });
    });
  }

  /**
   * 桌面卡屏：热力图从右侧滑入；未分屏时液冷循环，分屏后切封面并播热力图一次（右下角控制器）。
   * 手机不卡屏：液冷循环；热力图播一次不循环，用同一套控制器。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>, reduced: boolean }} ctx
   */
  function initMagflowTechPin(root, ctx) {
    const { cleanups, reduced } = ctx;
    const pins = Array.from(root.querySelectorAll('[data-mft-tech-pin]'));
    if (!pins.length) return;

    const mq = window.matchMedia(MOBILE_MQ);
    /* 第二屏到位后再播；回退要滚过半程才停，避免刚滑到就停 */
    const SPLIT_ON = 0.98;
    const SPLIT_OFF = 0.5;
    /* 后 1/4 行程锁在分屏完成，滑超也不立刻往回抽 */
    const SPLIT_HOLD = 0.25;

    const posterOf = (video) => {
      const media = video instanceof HTMLElement ? video.closest('[data-mft-tech-media]') : null;
      const poster = media ? media.querySelector('[data-mft-tech-loop-poster]') : null;
      return poster instanceof HTMLElement ? poster : null;
    };

    const splitPosterOf = (video) => {
      const media = video instanceof HTMLElement ? video.closest('[data-mft-tech-media]') : null;
      const poster = media ? media.querySelector('[data-mft-tech-split-poster]') : null;
      return poster instanceof HTMLElement ? poster : null;
    };

    const pauseKeep = (video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      try {
        video.pause();
      } catch (_) {}
    };

    const showPoster = (video, useSplit) => {
      if (!(video instanceof HTMLVideoElement)) return;
      video.dataset.mftTechSession = '';
      video._mftUserPaused = false;
      video._mftTechLoading = false;
      const poster = posterOf(video);
      const splitPoster = splitPosterOf(video);
      video.classList.remove('is-visible');
      if (useSplit && splitPoster) {
        if (poster) poster.classList.add('is-hidden');
        splitPoster.classList.add('is-shown');
      } else {
        if (splitPoster) splitPoster.classList.remove('is-shown');
        if (poster) poster.classList.remove('is-hidden');
      }
      /* 等交叉淡结束再暂停/回到片头，避免切封面时跳帧 */
      if (video._mftPosterTimer) window.clearTimeout(video._mftPosterTimer);
      video._mftPosterTimer = window.setTimeout(() => {
        video._mftPosterTimer = 0;
        pauseKeep(video);
        try {
          if (video.readyState >= 1) video.currentTime = 0;
        } catch (_) {}
      }, reduced ? 0 : MFT_MOTION.techCrossfadeMs);
    };

    const playOnce = (video, loop) => {
      if (!(video instanceof HTMLVideoElement)) return;
      if (video.dataset.mftTechSession === '1') return;
      video.dataset.mftTechSession = '1';
      video._mftUserPaused = false;
      try {
        video.loop = !!loop;
        video.muted = true;
        video.playsInline = true;
      } catch (_) {}
      const poster = posterOf(video);
      const splitPoster = splitPosterOf(video);
      const reveal = () => {
        if (video.dataset.mftTechSession !== '1') return;
        if (splitPoster) splitPoster.classList.remove('is-shown');
        video.classList.add('is-visible');
        if (poster) poster.classList.add('is-hidden');
      };
      const start = () => {
        if (video.dataset.mftTechSession !== '1') return;
        reveal();
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      };
      if (video.readyState >= 2) start();
      else {
        if (video._mftTechLoading) return;
        video._mftTechLoading = true;
        video.addEventListener(
          'canplay',
          () => {
            video._mftTechLoading = false;
            start();
          },
          { once: true }
        );
        try {
          video.load();
        } catch (_) {}
      }
    };

    const resumeOrPlay = (video, loop) => {
      if (!(video instanceof HTMLVideoElement)) return;
      if (video.dataset.mftTechSession === '1') {
        if (video.ended || video._mftUserPaused) return;
        try {
          video.loop = !!loop;
        } catch (_) {}
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        return;
      }
      playOnce(video, loop);
    };

    pins.forEach((pin) => {
      if (!(pin instanceof HTMLElement)) return;
      const stage = pin.querySelector('.ug-magflow-template__tech-stage');
      if (!(stage instanceof HTMLElement)) return;

      const liqVideo = pin.querySelector(
        '.ug-magflow-template__tech-panel--liq [data-mft-tech-loop-video]'
      );
      const heatVideo = pin.querySelector(
        '.ug-magflow-template__tech-panel--heat [data-mft-tech-loop-video]'
      );

      const heatPanel = pin.querySelector('.ug-magflow-template__tech-panel--heat');
      const heatCtrl = pin.querySelector('[data-mft-tech-video-ctrl]');
      const RING = 138.23;

      const setHeatUi = (state) => {
        if (!(heatPanel instanceof HTMLElement)) return;
        heatPanel.classList.remove('is-playing', 'is-paused', 'is-ended', 'is-loading');
        if (state) heatPanel.classList.add(state);
      };

      const updateHeatProgress = () => {
        if (!(heatVideo instanceof HTMLVideoElement) || !(heatCtrl instanceof HTMLElement)) return;
        const circle = heatCtrl.querySelector('[data-mft-tech-progress]');
        if (!circle || !heatVideo.duration) return;
        const p = Math.min(1, Math.max(0, heatVideo.currentTime / heatVideo.duration));
        circle.style.strokeDashoffset = String(RING - p * RING);
      };

      if (heatPanel instanceof HTMLElement) setHeatUi('is-paused');

      let heatProgRaf = 0;
      if (heatVideo instanceof HTMLVideoElement) {
        const onPlay = () => setHeatUi('is-playing');
        const onWaiting = () => {
          if (heatPanel instanceof HTMLElement) heatPanel.classList.add('is-loading');
        };
        const onPlaying = () => {
          if (heatPanel instanceof HTMLElement) heatPanel.classList.remove('is-loading');
        };
        const onPause = () => {
          if (heatPanel instanceof HTMLElement) heatPanel.classList.remove('is-loading');
          if (!heatVideo.ended) setHeatUi('is-paused');
        };
        const onTime = () => {
          if (heatProgRaf) return;
          heatProgRaf = window.requestAnimationFrame(() => {
            heatProgRaf = 0;
            updateHeatProgress();
          });
        };
        heatVideo.addEventListener('play', onPlay);
        heatVideo.addEventListener('waiting', onWaiting);
        heatVideo.addEventListener('playing', onPlaying);
        heatVideo.addEventListener('pause', onPause);
        heatVideo.addEventListener('timeupdate', onTime);
        cleanups.push(() => {
          if (heatProgRaf) window.cancelAnimationFrame(heatProgRaf);
          heatVideo.removeEventListener('play', onPlay);
          heatVideo.removeEventListener('waiting', onWaiting);
          heatVideo.removeEventListener('playing', onPlaying);
          heatVideo.removeEventListener('pause', onPause);
          heatVideo.removeEventListener('timeupdate', onTime);
        });
      }

      if (heatCtrl instanceof HTMLElement && heatVideo instanceof HTMLVideoElement) {
        const onCtrl = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (heatVideo.paused || heatVideo.ended) {
            heatVideo._mftUserPaused = false;
            if (heatVideo.ended) {
              try {
                heatVideo.currentTime = 0;
              } catch (_) {}
            }
            const playPromise = heatVideo.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
          } else {
            heatVideo._mftUserPaused = true;
            pauseKeep(heatVideo);
          }
        };
        heatCtrl.addEventListener('click', onCtrl);
        cleanups.push(() => heatCtrl.removeEventListener('click', onCtrl));
      }

      [liqVideo, heatVideo].forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        const onEnded = () => {
          if (video.loop) return;
          pauseKeep(video);
          if (video === heatVideo) setHeatUi('is-ended');
        };
        video.addEventListener('ended', onEnded);
        cleanups.push(() => video.removeEventListener('ended', onEnded));
      });

      let ticking = false;
      let splitDone = false;
      let stageVisible = false;
      /** @type {Array<() => void>} */
      let mediaCleanups = [];

      const setProgress = (value) => {
        pin.style.setProperty('--mft-tech-p', String(value));
      };

      const resetMediaObservers = () => {
        mediaCleanups.forEach((fn) => {
          try {
            fn();
          } catch (_) {}
        });
        mediaCleanups = [];
      };

      pin.dataset.mftLiqReady = reduced ? '1' : '';

      const tryLiqPlay = () => {
        if (pin.dataset.mftLiqReady !== '1') return;
        if (mq.matches) {
          resumeOrPlay(liqVideo, true);
          return;
        }
        if (stageVisible && !splitDone) resumeOrPlay(liqVideo, true);
      };
      pin._mftTryLiqPlay = tryLiqPlay;

      const observeOnce = (video, gated, loop) => {
        const shouldLoop = !!loop;
        if (!(video instanceof HTMLVideoElement) || typeof IntersectionObserver !== 'function') {
          if (!gated || pin.dataset.mftLiqReady === '1') playOnce(video, shouldLoop);
          return;
        }
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (gated && pin.dataset.mftLiqReady !== '1') return;
                resumeOrPlay(video, shouldLoop);
              } else if (!video.ended) pauseKeep(video);
            });
          },
          { threshold: 0.15 }
        );
        io.observe(video);
        mediaCleanups.push(() => io.disconnect());
      };

      const applySplitPlayback = (shouldSplit) => {
        if (shouldSplit === splitDone) return;
        splitDone = shouldSplit;
        if (splitDone) {
          showPoster(liqVideo, true);
          if (stageVisible) playOnce(heatVideo, false);
          pin.dataset.mftHeatReady = '1';
          const heatCopy = pin.querySelector('[data-mft-reveal-when="tech-heat"]');
          if (heatCopy && typeof heatCopy._mftPlayHeatReveal === 'function') {
            heatCopy._mftPlayHeatReveal();
          }
        } else {
          showPoster(heatVideo);
          setHeatUi('is-paused');
          if (stageVisible) tryLiqPlay();
        }
      };

      const readProgress = () => {
        if (mq.matches) return 0;
        if (reduced) return 1;
        const pinH = pin.offsetHeight;
        const stageH = stage.offsetHeight;
        const scrollable = pinH - stageH;
        if (scrollable <= 1) return 1;
        const travel = Math.max(1, scrollable * (1 - SPLIT_HOLD));
        const top = pin.getBoundingClientRect().top;
        return Math.min(1, Math.max(0, -top / travel));
      };

      const update = () => {
        ticking = false;
        const p = readProgress();
        setProgress(p);
        if (mq.matches) return;
        if (reduced) {
          applySplitPlayback(true);
          return;
        }
        if (!splitDone && p >= SPLIT_ON) applySplitPlayback(true);
        else if (splitDone && p < SPLIT_OFF) applySplitPlayback(false);
      };

      const bindDesktopStage = () => {
        if (typeof IntersectionObserver !== 'function') return;
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              stageVisible = entry.isIntersecting;
              if (stageVisible) {
                if (splitDone) resumeOrPlay(heatVideo, false);
                else if (!reduced) tryLiqPlay();
              } else {
                pauseKeep(liqVideo);
                pauseKeep(heatVideo);
              }
            });
          },
          { threshold: 0, rootMargin: '25% 0px 25% 0px' }
        );
        io.observe(stage);
        mediaCleanups.push(() => io.disconnect());
      };

      const applyMode = () => {
        resetMediaObservers();
        splitDone = false;
        stageVisible = false;
        showPoster(liqVideo);
        showPoster(heatVideo);
        if (mq.matches) {
          setProgress(0);
          observeOnce(liqVideo, !reduced, true);
          observeOnce(heatVideo, false, false);
          return;
        }
        update();
        bindDesktopStage();
        if (typeof IntersectionObserver !== 'function') {
          stageVisible = true;
          if (splitDone) playOnce(heatVideo, false);
          else if (!reduced) tryLiqPlay();
        }
        scheduleMftScrollTriggerRefresh();
      };

      const onScroll = () => {
        if (mq.matches || ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      applyMode();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', applyMode);
      cleanups.push(() => {
        resetMediaObservers();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', applyMode);
        [liqVideo, heatVideo].forEach((video) => {
          if (!(video instanceof HTMLVideoElement)) return;
          if (video._mftPosterTimer) {
            window.clearTimeout(video._mftPosterTimer);
            video._mftPosterTimer = 0;
          }
        });
        showPoster(liqVideo);
        showPoster(heatVideo);
      });
    });
  }

  function initMagflowSpeedMeter(root, ctx) {
    const { cleanups, reduced } = ctx;
    const meters = Array.from(root.querySelectorAll('[data-mft-speed-meter]'));
    if (!meters.length) return;

    /* 进视口：时间数字从 0 滚到目标，进度条 CSS 同步填充 */
    const DURATION = MFT_MOTION.meterMs;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const formatCount = (target, progress) => {
      const value = target * progress;
      if (Number.isInteger(target)) return String(Math.round(value));
      const decimals = String(target).split('.')[1]?.length || 1;
      return value.toFixed(decimals);
    };

    const setCounts = (meter, progress) => {
      meter.querySelectorAll('[data-mft-speed-count]').forEach((el) => {
        const target = Number(el.getAttribute('data-mft-speed-count'));
        if (!Number.isFinite(target)) return;
        el.textContent = formatCount(target, progress);
      });
    };

    const finish = (meter) => {
      meter.classList.add('is-played');
      setCounts(meter, 1);
    };

    const stop = (meter) => {
      if (meter._mftSpeedRaf) {
        window.cancelAnimationFrame(meter._mftSpeedRaf);
        meter._mftSpeedRaf = 0;
      }
    };

    const reset = (meter) => {
      stop(meter);
      meter.dataset.mftPlaying = '';
      meter.classList.remove('is-played');
      setCounts(meter, 0);
    };

    if (reduced) {
      meters.forEach(finish);
      return;
    }

    const play = (meter) => {
      if (meter.dataset.mftPlaying === '1') return;
      meter.dataset.mftPlaying = '1';
      stop(meter);
      meter.classList.add('is-played');
      setCounts(meter, 0);
      const started = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - started) / DURATION);
        setCounts(meter, easeOut(p));
        if (p < 1) {
          meter._mftSpeedRaf = window.requestAnimationFrame(tick);
          return;
        }
        meter._mftSpeedRaf = 0;
        setCounts(meter, 1);
      };
      meter._mftSpeedRaf = window.requestAnimationFrame(tick);
    };

    /* 有滚动淡入时等整块淡入完再跑；离开视口重置，再进来重播（对齐大标题扫光） */
    const waitReveal =
      window.gsap &&
      window.ScrollTrigger &&
      meters.some((meter) => meter.closest('[data-mft-reveal-play="speed-meter"]'));
    let unlocked = !waitReveal;

    meters.forEach((meter) => {
      meter._mftPlaySpeed = () => {
        unlocked = true;
        play(meter);
      };
    });

    if (typeof IntersectionObserver !== 'function') {
      if (unlocked) meters.forEach(play);
      cleanups.push(() => meters.forEach(stop));
      return;
    }

    const playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && unlocked) play(entry.target);
        });
      },
      { threshold: 0.35, rootMargin: '0px 0px -10% 0px' }
    );
    const resetObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) reset(entry.target);
        });
      },
      { threshold: 0 }
    );

    meters.forEach((meter) => {
      playObserver.observe(meter);
      resetObserver.observe(meter);
    });
    cleanups.push(() => {
      playObserver.disconnect();
      resetObserver.disconnect();
      meters.forEach(stop);
    });
  }

  /**
   * 手风琴：同一组里只展开一项，并同步切换配图（弹窗测试 / 充电场景共用）。
   *
   * @param {ParentNode} scope
   * @param {{ cleanups: Array<() => void> }} ctx
   * @returns {{ items: HTMLElement[], activate: (item: HTMLElement) => void }}
   */
  function bindMftExclusiveAccordion(scope, ctx) {
    const items = Array.from(scope.querySelectorAll('[data-mft-acc-item]')).filter(
      (el) => el instanceof HTMLElement
    );
    if (!items.length) return { items, activate: () => {} };
    const mediaItems = Array.from(scope.querySelectorAll('[data-mft-acc-media]')).filter(
      (el) => el instanceof HTMLElement
    );
    const showMedia = (item) => {
      if (!(item instanceof HTMLElement)) return;
      const idx = item.getAttribute('data-mft-acc-index') || '';
      mediaItems.forEach((media) => {
        media.classList.toggle('is-active', media.getAttribute('data-mft-acc-index') === idx);
      });
    };
    const activate = (item) => {
      if (!(item instanceof HTMLElement)) return;
      items.forEach((other) => {
        const isActive = other === item;
        other.classList.toggle('is-active', isActive);
        const otherBtn = other.querySelector('[data-mft-acc-trigger]');
        if (otherBtn instanceof HTMLElement) {
          otherBtn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        }
      });
      showMedia(item);
    };
    items.forEach((item) => {
      const trigger = item.querySelector('[data-mft-acc-trigger]');
      if (!(trigger instanceof HTMLElement)) return;
      const onToggle = (e) => {
        e.preventDefault();
        activate(item);
        if (typeof scope._mftOnAccUser === 'function') scope._mftOnAccUser(item);
      };
      trigger.addEventListener('click', onToggle);
      ctx.cleanups.push(() => trigger.removeEventListener('click', onToggle));
    });
    return { items, activate };
  }

  /**
   * 手风琴自动切：进视口后每 5 秒切下一项，激活项底部进度条同步填满。
   * 耐用测试弹窗、充电场景 ways-list 共用。
   *
   * @param {HTMLElement} layout
   * @param {{ items: HTMLElement[], activate: (item: HTMLElement) => void }} acc
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function bindMftAccAutoplay(layout, acc, ctx) {
    const { items, activate } = acc;
    if (items.length <= 1) return;
    const reduced = mftPrefersReducedMotion();
    let timer = 0;
    let inView = false;

    const currentIndex = () => {
      const i = items.findIndex((el) => el.classList.contains('is-active'));
      return i < 0 ? 0 : i;
    };

    const restartFill = () => {
      items.forEach((el) => el.classList.remove('is-filling'));
      const active = items[currentIndex()];
      if (!(active instanceof HTMLElement) || !inView || reduced) return;
      void active.offsetWidth;
      active.classList.add('is-filling');
    };

    const clear = () => {
      if (timer) {
        window.clearTimeout(timer);
        timer = 0;
      }
    };

    const start = () => {
      clear();
      if (!inView || reduced) return;
      restartFill();
      timer = window.setTimeout(() => {
        const next = items[(currentIndex() + 1) % items.length];
        activate(next);
        start();
      }, MFT_MOTION.autoplayMs);
    };

    layout._mftOnAccUser = () => start();
    layout._mftAccKick = () => {
      inView = true;
      start();
    };
    layout._mftAccPause = () => {
      inView = false;
      clear();
      items.forEach((el) => el.classList.remove('is-filling'));
    };

    if (typeof IntersectionObserver === 'function') {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
            if (inView) start();
            else {
              clear();
              items.forEach((el) => el.classList.remove('is-filling'));
            }
          });
        },
        { threshold: 0.35 }
      );
      io.observe(layout);
      ctx.cleanups.push(() => io.disconnect());
    } else {
      inView = true;
      start();
    }
    ctx.cleanups.push(() => {
      clear();
      layout._mftOnAccUser = null;
      layout._mftAccKick = null;
      layout._mftAccPause = null;
    });
  }

  /**
   * 安全耐用：Swiper 自动宽度 + Nexode Pro 填充进度条；第一张卡 + 打开弹窗；测试手风琴。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowDurability(root, ctx) {
    const { cleanups } = ctx;
    const sections = root.querySelectorAll('[data-mft-dur]');
    if (!sections.length) return;

    const instances = [];
    sections.forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const el = section.querySelector('[data-mft-dur-swiper]');
      if (!(el instanceof HTMLElement)) return;
      instances.push({
        section,
        el,
        prev: section.querySelector('[data-mft-dur-prev]'),
        next: section.querySelector('[data-mft-dur-next]'),
        progressFill: section.querySelector('[data-mft-dur-progress-fill]'),
        swiper: null
      });
    });
    if (!instances.length) return;

    let swiperWait = 0;

    const DUR_DESKTOP_MQ = '(min-width: 1024px)';

    const isDurDesktop = () =>
      typeof window.matchMedia === 'function' && window.matchMedia(DUR_DESKTOP_MQ).matches;

    const syncDurNav = (inst) => {
      const sw = inst.swiper;
      if (!sw) return;
      const start = sw.minTranslate();
      const end = sw.maxTranslate();
      const t = sw.translate;
      const atStart = t >= start - 1;
      const atEnd = t <= end + 1;
      if (inst.prev instanceof HTMLElement) {
        inst.prev.classList.toggle('swiper-button-disabled', atStart);
        inst.prev.toggleAttribute('disabled', atStart);
      }
      if (inst.next instanceof HTMLElement) {
        inst.next.classList.toggle('swiper-button-disabled', atEnd);
        inst.next.toggleAttribute('disabled', atEnd);
      }
    };

    const syncProgress = (inst) => {
      if (!(inst.progressFill instanceof HTMLElement)) return;
      const slides = inst.el.querySelectorAll('.swiper-slide');
      const total = slides.length || 1;
      const sw = inst.swiper;
      if (!sw) {
        inst.progressFill.style.width = `${(1 / total) * 100}%`;
        return;
      }
      const start = sw.minTranslate();
      const end = sw.maxTranslate();
      const p = end === start ? 0 : (start - sw.translate) / (start - end);
      const ratio = Math.min(1, Math.max(0, p));
      inst.progressFill.style.width = `${((1 / total + ratio * (1 - 1 / total)) * 100).toFixed(2)}%`;
    };

    const getDurStep = (sw) => {
      const slide = sw.slides && sw.slides[0];
      const width = slide instanceof HTMLElement ? slide.offsetWidth : 0;
      const gap = Number(sw.params.spaceBetween) || 0;
      return width + gap;
    };

    const animateDurTo = (inst, translate) => {
      const sw = inst.swiper;
      if (!sw) return;
      const speed = Number(sw.params.speed) || MFT_MOTION.slideMs;
      const target = Math.max(sw.maxTranslate(), Math.min(sw.minTranslate(), translate));
      sw.setTransition(speed);
      sw.setTranslate(target);
      sw.updateProgress();
      sw.updateActiveIndex();
      sw.updateSlidesClasses();
      syncDurNav(inst);
      syncProgress(inst);
    };

    const unbindDurNav = (inst) => {
      if (inst.prev instanceof HTMLElement && inst.onPrev) {
        inst.prev.removeEventListener('click', inst.onPrev);
      }
      if (inst.next instanceof HTMLElement && inst.onNext) {
        inst.next.removeEventListener('click', inst.onNext);
      }
      inst.onPrev = null;
      inst.onNext = null;
    };

    const destroyOne = (inst) => {
      unbindDurNav(inst);
      if (inst.swiper) {
        try {
          inst.swiper.destroy(true, true);
        } catch (_) {}
        inst.swiper = null;
      }
      inst.section.classList.remove('is-dur-swiper-inited');
    };

    const mountOne = (inst) => {
      if (typeof window.Swiper !== 'function') return;
      destroyOne(inst);
      const count = inst.el.querySelectorAll('.swiper-slide').length;
      if (count < 1) return;

      const opts = {
        slidesPerView: 'auto',
        spaceBetween: 20,
        slidesPerGroup: 1,
        watchOverflow: false,
        observer: true,
        observeParents: true,
        roundLengths: true,
        preventClicks: false,
        preventClicksPropagation: false,
        speed: MFT_MOTION.slideMs,
        breakpoints: {
          0: { spaceBetween: 10 },
          1024: { spaceBetween: 20 }
        },
        on: {
          init() {
            syncDurNav(inst);
            syncProgress(inst);
          },
          resize() {
            syncDurNav(inst);
            syncProgress(inst);
          },
          transitionEnd() {
            syncDurNav(inst);
            syncProgress(inst);
          }
        }
      };
      try {
        inst.swiper = new window.Swiper(inst.el, opts);
        inst.section.classList.add('is-dur-swiper-inited');
        inst.onPrev = (e) => {
          e.preventDefault();
          const sw = inst.swiper;
          if (!sw) return;
          const start = sw.minTranslate();
          if (sw.translate >= start - 1) return;
          if (isDurDesktop()) {
            animateDurTo(inst, start);
            return;
          }
          animateDurTo(inst, Math.min(start, sw.translate + getDurStep(sw)));
        };
        inst.onNext = (e) => {
          e.preventDefault();
          const sw = inst.swiper;
          if (!sw) return;
          const end = sw.maxTranslate();
          if (sw.translate <= end + 1) return;
          if (isDurDesktop()) {
            animateDurTo(inst, end);
            return;
          }
          animateDurTo(inst, Math.max(end, sw.translate - getDurStep(sw)));
        };
        if (inst.prev instanceof HTMLElement) {
          inst.prev.addEventListener('click', inst.onPrev);
        }
        if (inst.next instanceof HTMLElement) {
          inst.next.addEventListener('click', inst.onNext);
        }
        window.requestAnimationFrame(() => {
          if (inst.swiper) {
            inst.swiper.update();
            syncDurNav(inst);
            syncProgress(inst);
          }
        });
      } catch (_) {}
    };

    const mount = () => {
      instances.forEach(mountOne);
    };

    if (typeof window.Swiper === 'function') {
      mount();
    } else {
      swiperWait = window.setInterval(() => {
        if (typeof window.Swiper === 'function') {
          clearInterval(swiperWait);
          swiperWait = 0;
          mount();
        }
      }, MFT_MOTION.swiperPollMs);
      const waitMax = window.setTimeout(() => {
        if (swiperWait) {
          clearInterval(swiperWait);
          swiperWait = 0;
        }
      }, 10000);
      cleanups.push(() => clearTimeout(waitMax));
    }
    cleanups.push(() => {
      if (swiperWait) clearInterval(swiperWait);
      instances.forEach(destroyOne);
    });

    sections.forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const modal = section.querySelector('[data-mft-dur-modal]');
      const openBtn = section.querySelector('[data-mft-dur-open]');
      if (modal instanceof HTMLElement && openBtn instanceof HTMLElement) {
        let unlockScroll = null;
        let closeTimer = 0;
        const isMobileSheet = () =>
          typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;
        const openModal = () => {
          if (!unlockScroll) unlockScroll = mftLockPageScroll();
          if (closeTimer) {
            window.clearTimeout(closeTimer);
            closeTimer = 0;
          }
          modal.removeAttribute('hidden');
          modal.setAttribute('aria-hidden', 'false');
          const dialog = modal.querySelector('[data-mft-dur-dialog]');
          if (dialog instanceof HTMLElement) dialog.focus();
          const kickOpen = () => {
            modal.classList.add('is-open');
            window.requestAnimationFrame(() => {
              modal.querySelectorAll('[data-mft-acc-scope]').forEach((scope) => {
                if (typeof scope._mftAccKick === 'function') scope._mftAccKick();
              });
            });
          };
          /* 手机端先落在屏外，再挂 is-open 从下往上滑入 */
          if (isMobileSheet() && !mftPrefersReducedMotion()) {
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(kickOpen);
            });
          } else {
            kickOpen();
          }
        };
        const hideModal = () => {
          if (closeTimer) {
            window.clearTimeout(closeTimer);
            closeTimer = 0;
          }
          modal.setAttribute('hidden', '');
          modal.setAttribute('aria-hidden', 'true');
          modal.classList.remove('is-open');
          if (unlockScroll) {
            unlockScroll();
            unlockScroll = null;
          }
        };
        const closeModal = () => {
          if (modal.hasAttribute('hidden')) return;
          modal.querySelectorAll('[data-mft-acc-scope]').forEach((scope) => {
            if (typeof scope._mftAccPause === 'function') scope._mftAccPause();
          });
          modal.classList.remove('is-open');
          modal.setAttribute('aria-hidden', 'true');
          const dialog = modal.querySelector('[data-mft-dur-dialog]');
          if (isMobileSheet() && !mftPrefersReducedMotion() && dialog instanceof HTMLElement) {
            const onEnd = (e) => {
              if (!(e instanceof TransitionEvent)) return;
              if (e.target !== dialog || e.propertyName !== 'transform') return;
              dialog.removeEventListener('transitionend', onEnd);
              hideModal();
            };
            dialog.addEventListener('transitionend', onEnd);
            closeTimer = window.setTimeout(() => {
              dialog.removeEventListener('transitionend', onEnd);
              hideModal();
            }, MFT_MOTION.slideMs + 80);
            return;
          }
          hideModal();
        };
        const onOpen = (e) => {
          e.preventDefault();
          openModal();
        };
        openBtn.addEventListener('click', onOpen);
        cleanups.push(() => openBtn.removeEventListener('click', onOpen));
        section.querySelectorAll('[data-mft-dur-close]').forEach((btn) => {
          const onClose = (e) => {
            e.preventDefault();
            closeModal();
          };
          btn.addEventListener('click', onClose);
          cleanups.push(() => btn.removeEventListener('click', onClose));
        });
        const onEsc = (e) => {
          if (!(e instanceof KeyboardEvent) || e.key !== 'Escape') return;
          if (modal.hasAttribute('hidden')) return;
          closeModal();
        };
        window.addEventListener('keydown', onEsc);
        cleanups.push(() => {
          window.removeEventListener('keydown', onEsc);
          hideModal();
        });
      }

      section.querySelectorAll('[data-mft-acc-scope]').forEach((scope) => {
        const acc = bindMftExclusiveAccordion(scope, ctx);
        if (scope instanceof HTMLElement && scope.classList.contains('ug-magflow-template__dur-tests-layout')) {
          bindMftAccAutoplay(scope, acc, ctx);
        }
      });
    });
  }

  /**
   * 充电场景：每行一组手风琴，切换方式与安全耐用弹窗一致。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowWays(root, ctx) {
    root.querySelectorAll('[data-mft-way-row]').forEach((row) => {
      const acc = bindMftExclusiveAccordion(row, ctx);
      if (row instanceof HTMLElement) bindMftAccAutoplay(row, acc, ctx);
    });
  }

  /**
   * 法务角标统一平滑滚到 #mft-legal。
   * 技术角标走原生 href，专题页 html 为 scroll-behavior:auto 会瞬间跳到位；
   * 充电场景 ⁵ 原先走 JS smooth，两套路径观感不一致。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>, reduced: boolean }} ctx
   */
  function initMagflowLegalJump(root, ctx) {
    const isLegalJump = (el) => {
      if (!(el instanceof Element)) return false;
      if (el.hasAttribute('data-mft-legal-jump')) return true;
      if (!(el instanceof HTMLAnchorElement)) return false;
      const href = el.getAttribute('href') || '';
      return href === '#mft-legal' || href.endsWith('#mft-legal');
    };

    const onLegalJump = (e) => {
      const jump = e.target instanceof Element ? e.target.closest('a, [data-mft-legal-jump]') : null;
      if (!jump || !root.contains(jump) || !isLegalJump(jump)) return;
      e.preventDefault();
      e.stopPropagation();
      const legal = root.querySelector('#mft-legal') || document.getElementById('mft-legal');
      if (!(legal instanceof HTMLElement)) return;
      legal.scrollIntoView({
        behavior: ctx.reduced ? 'auto' : 'smooth',
        block: 'start'
      });
    };

    root.addEventListener('click', onLegalJump);
    ctx.cleanups.push(() => root.removeEventListener('click', onLegalJump));
  }

  /**
   * 场景图轮播：整组前后各复制一份做无缝循环（保证跳转时左右露边也一致）+ 拖拽 + 自动播。
   * Tab 切场景；手机左对齐并露出下一张。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>, reduced: boolean }} ctx
   */
  function initMagflowScenes(root, ctx) {
    const cleanups = ctx.cleanups;
    const reduced = ctx.reduced;
    const mqMo = window.matchMedia(MOBILE_MQ);

    root.querySelectorAll('[data-mft-scenes]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const viewport = section.querySelector('[data-mft-scenes-viewport]');
      const track = section.querySelector('[data-mft-scenes-track]');
      const navPrevList = Array.from(section.querySelectorAll('[data-mft-scenes-prev]'));
      const navNextList = Array.from(section.querySelectorAll('[data-mft-scenes-next]'));
      const tabs = Array.from(section.querySelectorAll('[data-mft-scenes-tab]'));
      const cards = Array.from(section.querySelectorAll('[data-mft-scenes-card]')).filter(
        (card) => card instanceof HTMLElement && !card.hasAttribute('data-mft-scenes-clone')
      );
      if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || !cards.length) {
        return;
      }

      const realCount = cards.length;
      const loopEnabled = realCount > 1;

      const resetCloneCta = (cloneRoot) => {
        if (!(cloneRoot instanceof HTMLElement)) return;
        cloneRoot.classList.remove('is-active');
        cloneRoot.setAttribute('aria-hidden', 'true');
        cloneRoot.setAttribute('data-mft-scenes-clone', '1');
        cloneRoot.querySelectorAll('[data-ug-product-cta]').forEach((el) => {
          el.removeAttribute('data-ug-product-cta');
        });
        cloneRoot.querySelectorAll('[data-ug-buy-dropdown]').forEach((wrap) => {
          if (!(wrap instanceof HTMLElement)) return;
          wrap.classList.remove('is-open');
        });
      };
      const cloneCardSet = () => {
        const frag = document.createDocumentFragment();
        cards.forEach((card) => {
          const clone = card.cloneNode(true);
          resetCloneCta(clone);
          frag.appendChild(clone);
        });
        return frag;
      };

      track.querySelectorAll('[data-mft-scenes-clone]').forEach((el) => el.remove());
      if (loopEnabled) {
        track.insertBefore(cloneCardSet(), track.firstChild);
        track.appendChild(cloneCardSet());
      }
      cleanups.push(() => {
        track.querySelectorAll('[data-mft-scenes-clone]').forEach((el) => el.remove());
      });

      const slides = Array.from(track.children).filter((slide) => slide instanceof HTMLElement);
      let currentSlide = loopEnabled ? realCount : 0;
      let activeIndex = 0;
      let autoTimer = 0;
      let inView = false;
      let isDragging = false;
      let dragPointerId = null;
      let dragStartX = 0;
      let dragBaseX = 0;
      let dragDeltaX = 0;

      const getRealIndexBySlide = (slideIndex) => {
        if (!loopEnabled) return Math.max(0, Math.min(slideIndex, realCount - 1));
        return ((slideIndex % realCount) + realCount) % realCount;
      };

      const getTargetTranslateX = (slideIndex) => {
        const activeCard = slides[slideIndex];
        if (!(activeCard instanceof HTMLElement) || viewport.clientWidth <= 0) return 0;
        if (mqMo.matches) {
          const pad = Number.parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
          return -(activeCard.offsetLeft - pad);
        }
        return -(activeCard.offsetLeft + activeCard.offsetWidth / 2 - viewport.clientWidth / 2);
      };

      const getCurrentTranslateX = () => {
        const tf = window.getComputedStyle(track).transform;
        if (!tf || tf === 'none') return getTargetTranslateX(currentSlide);
        const m2d = tf.match(/^matrix\((.+)\)$/);
        if (m2d && m2d[1]) {
          const parts = m2d[1].split(',').map((s) => Number.parseFloat(s.trim()));
          return Number.isFinite(parts[4]) ? parts[4] : getTargetTranslateX(currentSlide);
        }
        const m3d = tf.match(/^matrix3d\((.+)\)$/);
        if (m3d && m3d[1]) {
          const parts = m3d[1].split(',').map((s) => Number.parseFloat(s.trim()));
          return Number.isFinite(parts[12]) ? parts[12] : getTargetTranslateX(currentSlide);
        }
        return getTargetTranslateX(currentSlide);
      };

      const scrollActiveTabIntoCenter = (immediate) => {
        const tab = tabs[activeIndex];
        const scroller = tab instanceof HTMLElement ? tab.parentElement : null;
        if (!(tab instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;
        const max = scroller.scrollWidth - scroller.clientWidth;
        if (max <= 1) return;
        const tabRect = tab.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        const next = scroller.scrollLeft + (tabRect.left - scrollerRect.left) - (scroller.clientWidth - tabRect.width) / 2;
        const left = Math.max(0, Math.min(max, next));
        if (typeof scroller.scrollTo === 'function') {
          scroller.scrollTo({ left, behavior: immediate ? 'auto' : 'smooth' });
          return;
        }
        scroller.scrollLeft = left;
      };

      const updateUiBySlide = (immediate) => {
        activeIndex = getRealIndexBySlide(currentSlide);
        tabs.forEach((btn, i) => {
          if (!(btn instanceof HTMLElement)) return;
          const on = i === activeIndex;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        cards.forEach((card, i) => {
          card.classList.toggle('is-active', i === activeIndex);
        });
        scrollActiveTabIntoCenter(!!immediate);
      };

      const updateTrackTranslate = (immediate, slideIndex = currentSlide) => {
        const targetX = getTargetTranslateX(slideIndex);
        if (immediate) {
          track.style.transition = 'none';
          track.style.transform = `translate3d(${targetX}px, 0, 0)`;
          /* 强制回流后再恢复过渡，避免从克隆位补间回真卡 */
          void track.offsetWidth;
          track.style.removeProperty('transition');
          return;
        }
        track.style.transform = `translate3d(${targetX}px, 0, 0)`;
      };

      const snapLoopIfNeeded = () => {
        if (!loopEnabled) return false;
        if (currentSlide >= realCount * 2) {
          currentSlide -= realCount;
          updateTrackTranslate(true, currentSlide);
          return true;
        }
        if (currentSlide < realCount) {
          currentSlide += realCount;
          updateTrackTranslate(true, currentSlide);
          return true;
        }
        return false;
      };

      const activateByRealIndex = (idx, immediate) => {
        const safeIdx = Math.max(0, Math.min(idx, realCount - 1));
        currentSlide = loopEnabled ? safeIdx + realCount : safeIdx;
        updateUiBySlide(immediate);
        updateTrackTranslate(immediate, currentSlide);
      };

      const shiftSlide = (delta) => {
        if (!loopEnabled) {
          currentSlide = Math.max(0, Math.min(currentSlide + delta, realCount - 1));
        } else {
          currentSlide += delta;
        }
        updateUiBySlide(false);
        updateTrackTranslate(false, currentSlide);
      };

      const clearAutoPlay = () => {
        if (autoTimer) {
          window.clearInterval(autoTimer);
          autoTimer = 0;
        }
        section.classList.remove('is-autoplaying');
      };

      const startAutoPlay = () => {
        clearAutoPlay();
        if (!inView || realCount <= 1 || reduced) return;
        /* 先摘掉再加回，让当前 tab 进度条从 0 重跑 */
        void section.offsetWidth;
        section.classList.add('is-autoplaying');
        autoTimer = window.setInterval(() => {
          shiftSlide(1);
        }, MFT_MOTION.autoplayMs);
      };

      tabs.forEach((btn, idx) => {
        const onClick = () => {
          activateByRealIndex(idx, false);
          startAutoPlay();
        };
        btn.addEventListener('click', onClick);
        cleanups.push(() => btn.removeEventListener('click', onClick));
      });

      const bindNavBtn = (btn, delta) => {
        if (!(btn instanceof HTMLElement) || realCount <= 1) return;
        const onNavClick = (ev) => {
          if (ev instanceof MouseEvent && ev.button !== 0) return;
          shiftSlide(delta);
          startAutoPlay();
        };
        btn.addEventListener('click', onNavClick);
        cleanups.push(() => btn.removeEventListener('click', onNavClick));
      };
      navPrevList.forEach((btn) => bindNavBtn(btn, -1));
      navNextList.forEach((btn) => bindNavBtn(btn, 1));

      const onTrackTransitionEnd = (ev) => {
        if (!(ev instanceof TransitionEvent) || ev.target !== track || ev.propertyName !== 'transform') {
          return;
        }
        snapLoopIfNeeded();
      };
      track.addEventListener('transitionend', onTrackTransitionEnd);
      cleanups.push(() => track.removeEventListener('transitionend', onTrackTransitionEnd));

      const onResize = () => updateTrackTranslate(true);
      window.addEventListener('resize', onResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onResize));

      const onPointerDown = (ev) => {
        if (!(ev instanceof PointerEvent) || realCount <= 1) return;
        const targetEl = ev.target instanceof Element ? ev.target : null;
        if (targetEl && targetEl.closest('.ug-product-cta, a, button')) return;
        if (ev.pointerType === 'mouse' && ev.button !== 0) return;
        isDragging = true;
        dragPointerId = ev.pointerId;
        dragStartX = ev.clientX;
        dragDeltaX = 0;
        dragBaseX = getCurrentTranslateX();
        clearAutoPlay();
        track.style.transition = 'none';
        section.classList.add('is-scenes-dragging');
        try {
          viewport.setPointerCapture(ev.pointerId);
        } catch (_) {}
        ev.preventDefault();
      };

      const onPointerMove = (ev) => {
        if (!(ev instanceof PointerEvent) || !isDragging || dragPointerId !== ev.pointerId) return;
        dragDeltaX = ev.clientX - dragStartX;
        track.style.transform = `translate3d(${dragBaseX + dragDeltaX}px, 0, 0)`;
      };

      const finishDrag = (ev) => {
        if (!(ev instanceof PointerEvent) || !isDragging || dragPointerId !== ev.pointerId) return;
        isDragging = false;
        dragPointerId = null;
        section.classList.remove('is-scenes-dragging');
        try {
          viewport.releasePointerCapture(ev.pointerId);
        } catch (_) {}
        track.style.removeProperty('transition');
        const threshold = Math.max(40, viewport.clientWidth * 0.08);
        if (Math.abs(dragDeltaX) >= threshold) {
          shiftSlide(dragDeltaX < 0 ? 1 : -1);
        } else {
          updateTrackTranslate(false, currentSlide);
        }
        dragDeltaX = 0;
        startAutoPlay();
      };

      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', finishDrag);
      viewport.addEventListener('pointercancel', finishDrag);
      cleanups.push(() => viewport.removeEventListener('pointerdown', onPointerDown));
      cleanups.push(() => viewport.removeEventListener('pointermove', onPointerMove));
      cleanups.push(() => viewport.removeEventListener('pointerup', finishDrag));
      cleanups.push(() => viewport.removeEventListener('pointercancel', finishDrag));

      const onMouseEnter = () => clearAutoPlay();
      const onMouseLeave = () => startAutoPlay();
      section.addEventListener('mouseenter', onMouseEnter);
      section.addEventListener('mouseleave', onMouseLeave);
      cleanups.push(() => section.removeEventListener('mouseenter', onMouseEnter));
      cleanups.push(() => section.removeEventListener('mouseleave', onMouseLeave));

      const onVisibilityChange = () => {
        if (document.hidden) clearAutoPlay();
        else startAutoPlay();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      cleanups.push(() => document.removeEventListener('visibilitychange', onVisibilityChange));

      activateByRealIndex(0, true);
      window.requestAnimationFrame(() => activateByRealIndex(0, true));

      if (typeof IntersectionObserver === 'function') {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              inView = entry.isIntersecting;
              if (inView) startAutoPlay();
              else clearAutoPlay();
            });
          },
          { threshold: 0.35 }
        );
        io.observe(section);
        cleanups.push(() => io.disconnect());
      } else {
        inView = true;
        startAutoPlay();
      }
      cleanups.push(() => clearAutoPlay());
    });
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email);
  }

  function bindEmailBlock(block) {
    if (block.dataset.mftEmailInited === '1') return;

    const submitBtn = block.querySelector('.ug-magflow-template__signup-email-submit');
    const input = block.querySelector('.ug-magflow-template__signup-email-input');
    const errorMsg = block.querySelector('.ug-magflow-template__signup-email-error');
    const successMsg = block.querySelector('.ug-magflow-template__signup-email-message');
    if (!submitBtn || !input || !errorMsg || !successMsg) return;
    block.dataset.mftEmailInited = '1';

    const privacyCheckbox = block.querySelector('.ug-magflow-template__signup-email-checkbox');
    let abortCtrl = null;

    const formId = block.getAttribute('data-form-id') || '';
    const successText = block.getAttribute('data-success-text') || '';
    const failText = block.getAttribute('data-fail-text') || '';
    const privacyError = block.getAttribute('data-privacy-error') || '';
    const emailError = block.getAttribute('data-email-error') || '';

    const showError = (msg) => {
      errorMsg.textContent = msg;
      errorMsg.style.display = 'block';
      successMsg.style.display = 'none';
    };

    const showSuccess = (msg) => {
      successMsg.textContent = msg;
      successMsg.style.display = 'block';
      errorMsg.style.display = 'none';
    };

    const trackSubscription = (isSuccess) => {
      const track = window.ugGa4Track;
      if (!track || typeof track.subscriptionEvent !== 'function') return;
      try {
        track.subscriptionEvent({ isSuccess, position: 'magflow-template' });
      } catch (_) {}
    };

    const onSubmit = async () => {
      if (submitBtn.classList.contains('loading')) return;

      const email = input.value.trim();
      if (!isValidEmail(email)) {
        showError(emailError);
        return;
      }
      if (privacyCheckbox && !privacyCheckbox.checked) {
        showError(privacyError);
        return;
      }

      errorMsg.style.display = 'none';
      successMsg.style.display = 'none';
      submitBtn.classList.add('loading');
      submitBtn.setAttribute('aria-busy', 'true');

      try {
        abortCtrl = new AbortController();
        const response = await fetch(FORM_SUBMIT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: abortCtrl.signal,
          body: JSON.stringify({
            email,
            accepts_marketing: privacyCheckbox && privacyCheckbox.checked ? 'on' : 'off',
            form_id: formId
          })
        });

        let data = null;
        try {
          data = await response.json();
        } catch (_) {
          showError(failText);
          trackSubscription(false);
          return;
        }

        const isSuccess = data && data.status === 'success';
        if (isSuccess) {
          input.value = '';
          if (privacyCheckbox) privacyCheckbox.checked = false;
          showSuccess(successText);
        } else {
          const serverMsg =
            (data && data.errors && data.errors.email && data.errors.email[0]) ||
            (data && data.message) ||
            failText;
          showError(serverMsg);
        }
        trackSubscription(!!isSuccess);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        showError(failText);
        trackSubscription(false);
      } finally {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-busy');
      }
    };

    const onKeydown = (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      onSubmit();
    };

    submitBtn.addEventListener('click', onSubmit);
    input.addEventListener('keydown', onKeydown);

    block._mftEmailCleanup = () => {
      if (abortCtrl) abortCtrl.abort();
      submitBtn.removeEventListener('click', onSubmit);
      input.removeEventListener('keydown', onKeydown);
      submitBtn.classList.remove('loading');
      submitBtn.removeAttribute('aria-busy');
      delete block.dataset.mftEmailInited;
      delete block._mftEmailCleanup;
    };
  }

  /**
   * 留邮：Cirkle 提交、隐私勾选。
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initMagflowSignup(root, ctx) {
    const wraps = root.querySelectorAll('[data-mft-signup]');
    if (!wraps.length) return;

    wraps.forEach((wrap) => {
      wrap.querySelectorAll('[data-mft-email]').forEach((el) => bindEmailBlock(el));
    });

    ctx.cleanups.push(() => {
      wraps.forEach((wrap) => {
        wrap.querySelectorAll('[data-mft-email]').forEach((el) => {
          if (typeof el._mftEmailCleanup === 'function') el._mftEmailCleanup();
        });
      });
    });
  }

  class MagflowTemplateSection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mftInited === '1') return;
      this.dataset.mftInited = '1';
      /* 给不支持 :has() 的浏览器补页面标记，避免全站 header 仍吸顶盖住 KV */
      document.documentElement.classList.add('is-mft-page');

      const cleanups = [];
      this._mftCleanups = cleanups;
      const reduced = mftPrefersReducedMotion();
      initMagflowKvNav(this, { cleanups, reduced });
      initMagflowEditionsSwiper(this, { cleanups });
      initMagflowEditionsMoViewMore(this, { cleanups });
      initMagflowEditionsCodeCopy(this, { cleanups });
      initMagflowLoopVideos(this, { cleanups });
      initMagflowTvcModal(this, { cleanups });
      initMagflowTitleSweep(this, { cleanups, reduced });
      initMagflowTechPin(this, { cleanups, reduced });
      initMagflowSpeedMeter(this, { cleanups, reduced });
      initMagflowDurability(this, { cleanups });
      initMagflowWays(this, { cleanups });
      initMagflowLegalJump(this, { cleanups, reduced });
      initMagflowScenes(this, { cleanups, reduced });
      initMagflowSignup(this, { cleanups });
      if (window.UgProductCta && typeof window.UgProductCta.init === 'function') {
        window.UgProductCta.init(this, { cleanups });
      }
      const cancelGsapWait = whenGsapReady((ok) => {
        if (!this.isConnected) return;
        initMagflowScrollReveal(this, {
          cleanups,
          reduced: mftPrefersReducedMotion(),
          hasGsap: ok
        });
      });
      cleanups.push(cancelGsapWait);
    }

    disconnectedCallback() {
      delete this.dataset.mftInited;
      (this._mftCleanups || []).forEach((fn) => {
        try {
          fn();
        } catch (_) {}
      });
      this._mftCleanups = [];
      window.requestAnimationFrame(() => {
        if (!document.querySelector(TAG_NAME)) {
          document.documentElement.classList.remove('is-mft-page');
        }
      });
    }
  }

  customElements.define(TAG_NAME, MagflowTemplateSection);
})();
